import { useInfiniteQuery } from "@tanstack/react-query";
import {
  NativeSelect,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  Stack,
  useSidebar,
} from "@altimateai/lego";
import { getPlaygroundSessions } from "@/helpers/apis";
import { useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PlaygroundSidebarHeader } from "./PlaygroundSidebarHeader";
import { PlaygroundSidebarList } from "./PlaygroundSidebarList";
import { SessionLifecycleListFilter, SessionLifecycleStatus } from "./types";
import { useSingleSessionStatusMutation } from "./useSessionLifecycle";
import { usePlaygroundSidebarScroll } from "./usePlaygroundSidebarScroll";
import { usePlaygroundSidebarSummaryPoll } from "./usePlaygroundSidebarSummaryPoll";

const FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

const previousStatusFor = (listFilter: SessionLifecycleListFilter): SessionLifecycleStatus =>
  listFilter === "archived" ? "archived" : "active";

const PlaygroundSidebar = ({
  onNewChat,
}: {
  onNewChat: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}) => {
  const { session_id } = useParams();
  const [searchText, setSearchText] = useState("");
  const [listFilter, setListFilter] = useState<SessionLifecycleListFilter>("active");

  const observerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    data,
    isLoading: loading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    gcTime: 5 * 60 * 1000,
    queryKey: ["playground-sessions", { search_text: searchText, status: listFilter }],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      getPlaygroundSessions({
        search_text: searchText,
        page: pageParam,
        page_size: 20,
        status: listFilter,
      }),
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined;
      return lastPage.has_more ? lastPage.page + 1 : undefined;
    },
  });

  const { state } = useSidebar();
  const sessions = useMemo(() => data?.pages.flatMap(page => page?.sessions || []) || [], [data]);

  const singleMutation = useSingleSessionStatusMutation();
  const previousStatus = previousStatusFor(listFilter);

  const handleArchive = (id: string) =>
    singleMutation.mutate({ sessionId: id, status: "archived", previousStatus });
  const handleUnarchive = (id: string) =>
    singleMutation.mutate({ sessionId: id, status: "active", previousStatus });
  const handleDelete = (id: string) =>
    singleMutation.mutate({ sessionId: id, status: "deleted", previousStatus });

  // Infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );
  useEffect(() => {
    const element = observerRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(handleObserver, { threshold: 0 });
    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver]);
  usePlaygroundSidebarScroll(scrollContainerRef, loading, session_id);
  usePlaygroundSidebarSummaryPoll(sessions, `${searchText}|${listFilter}`);

  return (
    <Sidebar
      aria-label="Playground sidebar"
      // Inline-override the default --sidebar-width (16rem) to give Studio
      // chat rows breathing room: ~64px wider so titles wrap less and the
      // kebab menu sits cleanly inside the row instead of crowding text.
      style={{ "--sidebar-width": "20rem" } as React.CSSProperties}
      className={cn("al-bg-white al-z-[41] al-ml-[3rem]", state === "collapsed" && "!al-hidden")}
    >
      <PlaygroundSidebarHeader
        searchText={searchText}
        onSearchChange={setSearchText}
        onNewChat={onNewChat}
      />

      <SidebarContent ref={scrollContainerRef} role="region" aria-label="Chat history list">
        <SidebarGroup>
          <Stack align="center" justify="between" className="al-px-2 al-pr-1 al-mb-3">
            <SidebarGroupLabel className="!al-text-light-gray">Chat history</SidebarGroupLabel>
            <NativeSelect
              options={FILTER_OPTIONS}
              value={listFilter}
              onChange={(v: string) => {
                if (v === "active" || v === "archived") setListFilter(v);
              }}
              variant="ghost"
              size="sm"
              buttonProps={{ "aria-label": "Filter chat history" }}
              className="al-h-7 al-w-auto al-min-w-[6rem] al-text-sm al-font-medium"
            />
          </Stack>
          <SidebarGroupContent>
            <PlaygroundSidebarList
              sessions={sessions}
              loading={loading}
              error={error}
              searchText={searchText}
              listFilter={listFilter}
              isFetchingNextPage={isFetchingNextPage}
              observerRef={observerRef}
              openSessionId={session_id}
              onArchive={handleArchive}
              onUnarchive={handleUnarchive}
              onDelete={handleDelete}
            />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};

export default PlaygroundSidebar;
